import { TestLogic, IDirection, ILandoltContainer, NextLandoltIs } from './test-logic';
import { IVisusResultTelemetryData } from '../../result/util/typings';

export class EyeTestLogic extends TestLogic {

	private _smallestCorrectLandolt: ILandoltContainer;
	private _currentIndex: number;
	private _storagePrefix: string = 'eyeTest_';

	constructor() {
		super();
		this._smallestCorrectLandolt = this._landolts[0];
		this._currentIndex = 0;
	}

	type = (): string  => 'EyeTestLogic';

	current = (): ILandoltContainer => this._landolts[this._currentIndex];

	apply(direction: IDirection): boolean {
		const current: ILandoltContainer = this._landolts[this._currentIndex];
		++current.seen;
		if (current.direction.direction === direction) {
			++current.correctGuess;
			// User has guessed the current ring direction correctly twice in a row.
			if (current.correctGuess === 2) {
				current.identified = true;
				current.identifiedCorrectly = true;
				this._smallestCorrectLandolt = current;
				this._storageService.set(this._storagePrefix + 'scLandolt', JSON.stringify(current));
				return this._getNextLandolt(NextLandoltIs.SMALLER);
			} else {
				// User guessed that one right but only once. Change the ring direction and show the same size again.
				current.direction = TestLogic._getDifferentDirection(current.direction);
				return true;
			}
		} else {
			++current.wrongGuess;
			// User has guessed the biggest ring wrongly. Change the ring direction and show the same size again.
			if (this._currentIndex === 0 && current.wrongGuess === 1) {
				current.direction = TestLogic._getDifferentDirection(current.direction);
				return true;
			}

			// User has already guessed the current ring wrongly and now they did it a second time.
			if (current.wrongGuess === 2) {
				current.identified = true;
				current.identifiedWrongly = true;
			}
			// In case of a wrong guess we don’t show the same ring again but rather go one step bigger.
			return this._getNextLandolt(NextLandoltIs.BIGGER);
		}
	}

	resetState(): void {
		this._resetLandolts();
		this._storageService.del([this._storagePrefix + 'scLandolt']);
		this._globalLogic.removeTestFromFinishedList('eye-test');
		this._smallestCorrectLandolt = this._landolts[0];
		this._currentIndex = 0;
	}

	get smallestCorrectLandolt(): ILandoltContainer {
		const buf = this._storageService.get(this._storagePrefix + 'scLandolt');
		if (buf) {
			return JSON.parse(buf);
		}
		return this._smallestCorrectLandolt;
	}

	get smallestCorrectLandoltReducedOpacity(): ILandoltContainer {
		// TODO: This is a hack because of the interface
		return this._smallestCorrectLandolt;
	}

	getTelemetryData(): IVisusResultTelemetryData {
		if (!this.finished) {
			return { visus: null };
		}
		return {
			visus: +(this.smallestCorrectLandolt.visusOneMeter * this._globalLogic.distance / 100).toFixed(4)
		};
	}

	private _getNextLandolt(nextLandoltIs: NextLandoltIs = NextLandoltIs.SMALLER): boolean {
		if (nextLandoltIs === NextLandoltIs.SMALLER) {
			// User has just identified the smallest ring. Don’t show any more rings.
			if (this._currentIndex === this._max - 1) {
				this._markTestAsFinished();
				return false;
			}

			// User has identified the second last ring. Show them the last one.
			if (this._currentIndex === this._max - 2) {
				--this._currentIndex;
				if (!this._landolts[this._currentIndex].identified) {
					return true;
				}
				this._markTestAsFinished();
				return false;
			}

			// Show two sizes smaller
			this._currentIndex += 2;

			if (this._landolts[this._currentIndex].identified) {
				return this._getNextLandolt(NextLandoltIs.SMALLER);
			}
		} else {
			// User could not identify the biggest ring. Don‘t show any more rings.
			if (this._currentIndex === 0) {
				this._markTestAsFinished();
				return false;
			}

			// Show one size bigger
			--this._currentIndex;
			if (this._landolts[this._currentIndex].identified) {
				if (this._landolts[this._currentIndex].identifiedCorrectly) {
					this._markTestAsFinished();
					return false;
				}
				return this._getNextLandolt(NextLandoltIs.BIGGER);
			}
		}
		return true;
	}

}
